import { useChatShowMessage } from '@jobilla/nexus-sdk'
import { UIMessage } from 'ai'
import { useEffect, useMemo, useRef } from 'react'
import { TOOL_NAMES, ToolName } from '../../data'

const POLLING_INTERVAL_MS = 3000 // Poll every 3 seconds while tools are running

// Tool state utilities
type ToolState = 'output-available' | 'output-error' | 'input-streaming' | 'input-available' | null | undefined

// Extract the part type from UIMessage
type MessagePart = UIMessage['parts'][number]

// Type for tool parts that have state
type ToolPart = MessagePart & {
	type: string
	state?: ToolState
}

const hasState = (part: MessagePart): part is ToolPart => {
	return 'state' in part
}

// Map tool names to affected campaign resources
const getAffectedResources = (toolName: ToolName): Array<'feedAd' | 'storyAd' | 'landingPage' | 'form'> => {
	switch (toolName) {
		case TOOL_NAMES.editImage:
		case TOOL_NAMES.generateImages:
			// Images can affect feed and story ads
			return ['feedAd', 'storyAd']
		case TOOL_NAMES.generateAdCopy:
		case TOOL_NAMES.getAdCopy:
			// Ad copy affects feed and story ads
			return ['feedAd', 'storyAd']
		case TOOL_NAMES.generateLandingPage:
		case TOOL_NAMES.getLandingPageContent:
			return ['landingPage']
		case TOOL_NAMES.modifySurvey:
		case TOOL_NAMES.getSurveyContent:
			return ['form']
		default:
			return []
	}
}

const isImageTool = (toolName: ToolName): boolean => {
	return toolName === TOOL_NAMES.editImage || toolName === TOOL_NAMES.generateImages
}

const isTerminalToolState = (state: ToolState): boolean => {
	return state === 'output-available' || state === 'output-error'
}

// Check only the LATEST message for pending image tools
const hasPendingImageTools = (messages: UIMessage[]): boolean => {
	if (messages.length === 0) {
		return false
	}

	const latestMessage = messages[messages.length - 1]

	return latestMessage.parts?.some((p: MessagePart) => {
		if (
			String(p.type).startsWith('tool-')
			&& p.type !== 'tool-result'
		) {
			if (!hasState(p)) {
				return false
			}

			if (isTerminalToolState(p.state)) {
				return false
			}

			const toolName = p.type.replace('tool-', '') as ToolName
			return toolName === TOOL_NAMES.editImage || toolName === TOOL_NAMES.generateImages
		}
		return false
	}) ?? false
}

const getToolError = (messages: UIMessage[]): string | null => {
	const lastAssistant = messages.slice().reverse().find(m => m.role === 'assistant')
	if (!lastAssistant) { return null }

	const errorPart = lastAssistant.parts?.find((p: MessagePart): p is ToolPart => {
		return hasState(p) && p.state === 'output-error'
	})

	if (errorPart && 'errorText' in errorPart && errorPart.errorText) {
		// Don't treat cancelled tool calls as errors
		const errorText = errorPart.errorText as string
		if (errorText.includes('cancelled by user')) {
			return null
		}
		return extractErrorMessage(errorText)
	}

	// Fallback for old format
	type LegacyToolCall = {
		status?: string
		error_message?: string
	}

	const failedToolCall = messages
		.flatMap((m): LegacyToolCall[] => {
			// Check if message has legacy tool_calls property
			const msg = m as UIMessage & { tool_calls?: LegacyToolCall[] }
			return msg.tool_calls ?? []
		})
		.find((tc) => tc.status === 'failed')

	return failedToolCall?.error_message || null
}

const extractErrorMessage = (_errorText: string): string => {
	// Return generic message - full error details are already captured by Sentry
	// and may contain sensitive(ish) info (like the URL of the chat endpoint (CAI))
	return 'An error occurred while processing your request. Please try again.'
}

export function useToolStatusPolling({
	chatId,
	messages,
	onToolStateChange,
	onToolsCompleted,
	onStartPolling,
	onStopPolling,
	onToolTriggered,
	onToolCompleted,
}: {
	chatId: string
	messages: UIMessage[]
	onToolStateChange: (messages: UIMessage[]) => void
	onToolsCompleted: () => void
	onStartPolling: () => void
	onStopPolling: () => void
	onToolTriggered?: (toolName: ToolName, messageId: string) => void
	onToolCompleted?: (toolName: ToolName, messageId: string, success: boolean) => void
}) {
	const isPollingRef = useRef(false)
	const previousPendingStateRef = useRef(false)
	const wasRunningImageToolsRef = useRef(false)
	// Track tool states: Map<messageId-toolName, state>
	const toolStatesRef = useRef(new Map<string, ToolState>())

	// Detect if tools are pending in the current messages and get the message ID
	const pendingMessageId = useMemo(() => {
		if (messages.length === 0) {
			return null
		}

		// Find the last assistant message with pending tools
		const lastAssistantWithPendingTools = messages
			.slice()
			.reverse()
			.find(m => {
				return m.role === 'assistant' && m.parts?.some((p: MessagePart) => {
					if (!String(p.type).startsWith('tool-') || p.type === 'tool-result') {
						return false
					}

					if (!hasState(p)) {
						return false
					}

					return !isTerminalToolState(p.state)
				})
			})

		return lastAssistantWithPendingTools?.id || null
	}, [messages])

	// Detect if tools are pending in the current messages
	const hasPendingToolCalls = useMemo(() => {
		return pendingMessageId !== null
	}, [pendingMessageId])

	const hasPendingImageToolCalls = useMemo(() => {
		return hasPendingImageTools(messages)
	}, [messages])

	const errorMessage = useMemo(() => {
		if (hasPendingToolCalls) {
			return null
		}
		return getToolError(messages)
	}, [messages, hasPendingToolCalls])

	// Determine which resources have active tool calls (both chat and image tools)
	// Single iteration to populate both resource sets
	const { activeToolExecution, activeImageToolExecution } = useMemo(() => {
		const chatToolResources = {
			feedAd: false,
			storyAd: false,
			landingPage: false,
			form: false,
		}
		const imageToolResources = {
			feedAd: false,
			storyAd: false,
			landingPage: false,
			form: false,
		}

		messages.forEach(msg => {
			msg.parts?.forEach((part: MessagePart) => {
				// Skip non-tool parts and tool results
				if (!String(part.type).startsWith('tool-') || part.type === 'tool-result') {
					return
				}

				// Check if part has state
				if (!hasState(part)) {
					return
				}

				// Skip completed/errored tools
				if (isTerminalToolState(part.state)) {
					return
				}

				const toolName = part.type.replace('tool-', '') as ToolName
				const affectedResources = getAffectedResources(toolName)

				// Route to appropriate resource set based on tool type
				const targetResources = isImageTool(toolName) ? imageToolResources : chatToolResources

				affectedResources.forEach(resource => {
					targetResources[resource] = true
				})
			})
		})

		return {
			activeToolExecution: chatToolResources,
			activeImageToolExecution: imageToolResources,
		}
	}, [messages])

	// Polling query - fetches specific message by ID when enabled
	const { data: polledMessageData } = useChatShowMessage(
		chatId,
		pendingMessageId ?? '',
		{
			enabled: isPollingRef.current && !!pendingMessageId,
			refetchInterval: POLLING_INTERVAL_MS,
			staleTime: 0,
		},
	)

	// Extract the message from the response
	const polledMessage = polledMessageData?.message as UIMessage | null | undefined

	// Auto-detect pending tools and start/stop polling
	useEffect(() => {
		const hasPending = hasPendingToolCalls

		// Transition from not pending → pending: start polling
		if (hasPending && !previousPendingStateRef.current) {
			isPollingRef.current = true
			previousPendingStateRef.current = true
			onStartPolling()

			// Track image tools starting
			if (hasPendingImageToolCalls) {
				wasRunningImageToolsRef.current = true
			}
		}

		// Transition from pending → not pending: stop polling and trigger completion callback
		if (!hasPending && previousPendingStateRef.current) {
			isPollingRef.current = false
			previousPendingStateRef.current = false

			onStopPolling()
			onToolsCompleted()

			// Reset image tools tracking
			wasRunningImageToolsRef.current = false
		}

		// Update image tools tracking if still pending
		if (hasPending && hasPendingImageToolCalls && !wasRunningImageToolsRef.current) {
			wasRunningImageToolsRef.current = true
		}
	}, [hasPendingToolCalls, hasPendingImageToolCalls, onStartPolling, onStopPolling, onToolsCompleted])

	// Track individual tool lifecycle events
	useEffect(() => {
		if (!onToolTriggered && !onToolCompleted) {
			return // Skip if no callbacks provided
		}

		messages.forEach(message => {
			if (message.role !== 'assistant') { return }

			message.parts?.forEach((part: MessagePart) => {
				// Skip non-tool parts and tool results
				if (!String(part.type).startsWith('tool-') || part.type === 'tool-result') {
					return
				}

				// Check if part has state
				if (!hasState(part)) {
					return
				}

				const toolName = part.type.replace('tool-', '') as ToolName
				const toolId = `${message.id}-${toolName}`
				const previousState = toolStatesRef.current.get(toolId)
				const currentState = part.state

				// Detect tool triggered: transition from null/undefined to a non-terminal state
				if (
					onToolTriggered
					&& !previousState
					&& currentState
					&& !isTerminalToolState(currentState)
				) {
					onToolTriggered(toolName, message.id)
				}

				// Detect tool completed: transition to terminal state (output-available or output-error)
				if (
					onToolCompleted
					&& currentState
					&& isTerminalToolState(currentState) // Handles both 'output-available' and 'output-error'
					&& (!previousState || !isTerminalToolState(previousState))
				) {
					const success = currentState === 'output-available'
					onToolCompleted(toolName, message.id, success)
				}

				// Update tracked state
				toolStatesRef.current.set(toolId, currentState)
			})
		})
	}, [messages, onToolTriggered, onToolCompleted])

	// Update message with polling data
	// CRITICAL: Only update when polling is active (tools are running)
	// If polling is stopped, don't overwrite messages (preserves streamed continuations)
	// NOTE: messages is intentionally NOT in the dependency array to avoid infinite loops
	// This effect should only react to new polledMessage data, not to messages updates
	useEffect(() => {
		if (!polledMessage) {
			return
		}

		if (!isPollingRef.current) {
			return
		}

		// Update the specific message by ID
		const updatedMessages = messages.map(msg => msg.id === polledMessage.id ? polledMessage : msg)

		// Only call callback if the message actually changed
		const originalMessage = messages.find(m => m.id === polledMessage.id)
		const hasChanges = !originalMessage
			|| originalMessage.parts?.length !== polledMessage.parts?.length
			|| originalMessage.parts?.some((p, i) => {
				const polledPart = polledMessage.parts?.[i]
				if (!polledPart) { return true }

				// Check type
				if (p.type !== polledPart.type) { return true }

				// Check state for parts that have it (tool parts)
				if (hasState(p) && hasState(polledPart)) {
					return p.state !== polledPart.state
				}

				return false
			})

		if (hasChanges) {
			onToolStateChange(updatedMessages)
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [polledMessage, onToolStateChange])

	return {
		hasPendingTools: hasPendingToolCalls,
		activeToolExecution,
		activeImageToolExecution,
		errorMessage,
	}
}
